import type { History } from "history";
import {
  datasetDocumentViewerUrl,
  type RecordViewOptions,
} from "src/features/library/record/recordPath";
import { projectFileViewerUrl } from "src/views/assistant/utils/projectFileRoute";
import type { OpenFileAnnotation } from "src/views/assistant/projects/stores/projectsLayoutStore";
import { RecordHomeKind, type RecordHome } from "./types";

export interface RecordNavOptions extends RecordViewOptions {
  /** Router state to carry along (source-provenance annotations for the viewer overlay). */
  annotations?: OpenFileAnnotation[];
}

/**
 * The canonical URL for a resolved home + SDR id. The two routes have
 * different shapes and param support — the folder route only understands
 * `page`/`chunkId`, so `search`/`ref`/`fromCollection`/`newChat` are silently
 * dropped when routing to a folder-homed record.
 */
export const recordHomeUrl = (
  home: RecordHome,
  sdrId: number,
  opts: RecordNavOptions = {},
): string => {
  if (home.kind === RecordHomeKind.Folder) {
    // A non-numeric string chunkId can't be parsed — drop it rather than
    // serialize a NaN into the URL (projectFileViewerUrl has no validation
    // of its own).
    const chunkId =
      opts.chunkId != null && Number.isFinite(Number(opts.chunkId))
        ? Number(opts.chunkId)
        : null;
    return projectFileViewerUrl(home.folderId, sdrId, {
      page: opts.page,
      chunkId,
    });
  }
  return datasetDocumentViewerUrl(home.datasetId, sdrId, opts);
};

/**
 * Push the URL for a resolved record home. Returns false (no navigation, no
 * URL touched) when `home` is undefined, so the caller decides how to
 * represent "can't open this" — e.g. leaving a citation inert — rather than
 * navigating to a broken route.
 */
export const navigateToRecord = (
  history: History,
  home: RecordHome | undefined,
  sdrId: number,
  opts: RecordNavOptions = {},
): boolean => {
  if (!home) return false;
  const { annotations, ...urlOpts } = opts;
  history.push(
    recordHomeUrl(home, sdrId, urlOpts),
    annotations ? { annotations } : undefined,
  );
  return true;
};
